import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  CandlestickChart,
  ChevronLeft,
  Eye,
  FileText,
  FlaskConical,
  Gauge,
  Globe,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  OctagonX,
  ScrollText,
  Settings,
  ShieldAlert,
  Sun,
  Table2,
  Trophy,
  Workflow,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";
import { usePriceAlertToasts } from "@/features/charts/trading";
import { api, errorMessage } from "@/lib/api";
import { useChrome } from "@/lib/chrome";
import { hm, time } from "@/lib/format";
import { useBack, useKernel, useMediaQuery, useTheme, useTick } from "@/lib/hooks";
import { useLive } from "@/lib/live";
import { Badge, Button, Dot, Segmented, cn } from "../ui/core";
import { Pnl } from "../ui/data";
import { DropMenu, Popover, Sheet, Tip, confirm } from "../ui/overlay";

export const NAV = [
  { to: "/", label: "Command center", short: "Home", icon: LayoutDashboard, end: true },
  { to: "/watch", label: "Market watch", short: "Watch", icon: Eye },
  { to: "/charts", label: "Charts", short: "Charts", icon: CandlestickChart },
  { to: "/options", label: "Option chain", short: "Options", icon: BarChart3 },
  { to: "/markets", label: "Global markets", short: "Markets", icon: Globe },
  { to: "/strategies", label: "Strategies", short: "Strategies", icon: Workflow },
  { to: "/backtests", label: "Backtest lab", short: "Backtests", icon: FlaskConical },
  { to: "/trades", label: "Trades", short: "Trades", icon: Table2 },
  { to: "/performance", label: "Performance", short: "Performance", icon: Trophy },
  { to: "/reports", label: "Reports", short: "Reports", icon: FileText },
  { to: "/journal", label: "Journal", short: "Journal", icon: ScrollText },
  { to: "/risk", label: "Risk and limits", short: "Risk", icon: Gauge },
  { to: "/settings", label: "Settings", short: "Settings", icon: Settings },
];
type NavItem = (typeof NAV)[number];
const at = (to: string) => NAV.find((n) => n.to === to)!;

// the sidebar in groups, the way a trader's day runs: watch, build, review, control
export const GROUPS: { label: string | null; items: NavItem[] }[] = [
  { label: null, items: [at("/")] },
  { label: "Markets", items: ["/watch", "/charts", "/options", "/markets"].map(at) },
  { label: "Build", items: ["/strategies", "/backtests"].map(at) },
  { label: "Review", items: ["/trades", "/performance", "/reports", "/journal"].map(at) },
  { label: "Control", items: ["/risk", "/settings"].map(at) },
];

// the phone's tab bar; everything else is under More
// Home, Watch, Charts, Strategies, Trades (five plus More fit a 360 px screen)
export const TABS = ["/", "/watch", "/charts", "/strategies", "/trades"].map(at);
export const MORE = GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => !TABS.includes(n)) })).filter((g) => g.items.length);

// the phone's top bar: a title per route, and where its back arrow goes
const ROUTES: [RegExp, string, string | null][] = [
  [/^\/$/, "Command center", null],
  [/^\/watch/, "Market watch", null],
  [/^\/charts/, "Charts", null],
  [/^\/strategies\/[^/]+$/, "Strategy", "/strategies"],
  [/^\/strategies/, "Strategies", null],
  [/^\/backtests\/[^/]+$/, "Backtest", "/backtests"],
  [/^\/backtests/, "Backtest lab", null],
  [/^\/trades/, "Trades", null],
  [/^\/performance/, "Performance", null],
  [/^\/reports/, "Reports", null],
  [/^\/options/, "Option chain", null],
  [/^\/markets/, "Global markets", null],
  [/^\/risk/, "Risk and limits", null],
  [/^\/journal/, "Journal", null],
  [/^\/settings\/[^/]+$/, "Settings", "/settings"],
  [/^\/settings/, "Settings", null],
];

function Brand({ compact }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <BrandMark />
      {!compact && <span className="text-[15px] font-semibold tracking-tight">AlgoTrader</span>}
    </div>
  );
}

function Nav({ rail, onNavigate }: { rail?: boolean; onNavigate?: () => void }) {
  return (
    <nav className={cn("flex flex-col", rail ? "gap-2" : "gap-2.5")} aria-label="Main">
      {GROUPS.map((g, gi) => (
        <div key={gi} className="flex flex-col gap-0.5">
          {g.label &&
            (rail ? (
              <div className="mx-auto mb-1 h-px w-8 bg-line" aria-hidden />
            ) : (
              <div className="mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-faint uppercase">{g.label}</div>
            ))}
          {g.items.map(({ to, label, short, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNavigate}
              title={rail ? label : undefined}
              className={({ isActive }) =>
                cn(
                  "group relative flex items-center rounded-lg font-medium transition-colors",
                  // a narrow screen still names every page: the label sits under the icon
                  rail ? "min-h-[52px] flex-col justify-center gap-1 px-0.5 py-1.5 text-[10.5px] leading-none" : "h-8 gap-3 px-2.5 text-[13.5px] pointer-coarse:h-10",
                  isActive ? "bg-panel-3 text-fg" : "text-muted hover:bg-panel-2 hover:text-fg",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && !rail && <span className="absolute top-1.5 bottom-1.5 -left-3 w-[3px] rounded-r-full bg-accent" aria-hidden />}
                  <Icon className={cn("size-[18px] shrink-0", isActive ? "text-accent" : "text-faint group-hover:text-muted")} />
                  <span className={cn(rail ? "max-w-full truncate text-center" : "truncate")}>{rail ? short : label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function useSystemRows() {
  const status = useLive((s) => s.status);
  const engine = useLive((s) => s.snapshot?.engine);
  const at = useLive((s) => s.snapshotAt);
  useTick(2000);
  const stale = at > 0 && Date.now() - at > 6000;
  const rows: [string, "up" | "down" | "warn" | "muted", string][] = [
    ["Server", status === "open" && !stale ? "up" : status === "connecting" ? "warn" : "down", status === "open" ? (stale ? "no updates" : "connected") : status],
    ["Engine", engine?.state === "running" ? "up" : engine ? "warn" : "muted", engine?.state ?? "unknown"],
    [
      "Market data",
      engine?.source === "replay" ? "warn" : engine?.feed.connected ? "up" : engine?.session.open ? "down" : "muted",
      engine?.source === "replay" ? "replay" : engine?.feed.connected ? "streaming" : "offline",
    ],
    [
      "Broker",
      engine?.broker.logged_in ? "up" : engine?.broker.configured ? "warn" : "muted",
      engine?.broker.logged_in ? (engine.broker.client_code ?? "logged in") : engine?.broker.configured ? "logged out" : "not set up",
    ],
  ];
  return rows;
}

function StatusRows({ rows }: { rows: ReturnType<typeof useSystemRows> }) {
  return (
    <div className="space-y-2 text-[12.5px]">
      {rows.map(([k, tone, v]) => (
        <div key={k} className="flex items-center gap-2">
          <Dot tone={tone} pulse={tone === "up" && k === "Market data"} />
          <span className="text-muted">{k}</span>
          <span className="ml-auto truncate pl-3 font-medium">{v}</span>
        </div>
      ))}
    </div>
  );
}

/** The sidebar's system line: one dot for the worst of server, engine, feed
 * and broker, with the four on hover; the phone's More sheet lists them all. */
function SystemStatus({ rail, full, dot }: { rail?: boolean; full?: boolean; dot?: boolean }) {
  const rows = useSystemRows();
  if (full)
    return (
      <div className="rounded-xl border border-line bg-panel-2/60 p-3">
        <StatusRows rows={rows} />
      </div>
    );
  const bad = rows.find((r) => r[1] === "down") ?? rows.find((r) => r[1] === "warn");
  const worst = bad?.[1] ?? "up";
  const summary = bad ? `${bad[0]}: ${bad[2]}` : "All systems normal";
  return (
    <Popover
      align="start"
      className="w-64 p-3"
      trigger={
        <button
          className={cn(
            "flex rounded-lg text-left hover:bg-panel-2",
            rail ? "min-h-[48px] w-full flex-col items-center justify-center gap-1.5" : dot ? "grid size-10 shrink-0 place-items-center" : "h-9 w-full items-center gap-2.5 px-2.5",
          )}
          aria-label={`System status: ${summary}`}
          title={dot ? summary : undefined}
        >
          <Dot tone={worst} pulse={worst === "up"} className={rail || dot ? "size-2.5" : undefined} />
          {!dot && <span className={cn("min-w-0 truncate text-muted", rail ? "text-[10.5px] leading-none" : "flex-1 text-[12.5px]")}>{rail ? "Status" : summary}</span>}
        </button>
      }
    >
      <div className="mb-2 text-[12px] font-semibold">System</div>
      <StatusRows rows={rows} />
    </Popover>
  );
}

function SessionClock() {
  useTick(1000);
  const sess = useLive((s) => s.snapshot?.engine.session);
  const replay = useLive((s) => s.snapshot?.engine.replay);
  const engineTs = useLive((s) => s.snapshot?.engine.ts);
  const now = replay && engineTs ? new Date(engineTs) : new Date();
  let label = "Market closed";
  let tone: "up" | "muted" | "warn" = "muted";
  if (sess?.open) {
    label = `Open, closes ${hm(sess.closes_at)}`;
    tone = "up";
  } else if (sess?.trading_day && sess.opens_at && new Date(sess.opens_at) > now) {
    label = `Opens ${hm(sess.opens_at)}`;
    tone = "warn";
  } else if (sess?.next_open) {
    label = `Next open ${new Date(sess.next_open).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "2-digit", month: "short" })}`;
  }
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="mono text-[14px] font-medium">{time(now.toISOString())}</span>
      <span className="text-[11px] text-faint">IST</span>
      <Badge tone={tone === "up" ? "up" : tone === "warn" ? "warn" : "muted"} className="min-w-0">
        {tone === "up" && <Dot tone="up" pulse />}
        <span className="truncate">{replay ? `Replay ${replay.speed}x, ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label}</span>
      </Badge>
    </div>
  );
}

function KillSwitch() {
  const { k, mode } = useKernel();
  const engaged = k ? !k.risk.trading_enabled : false;
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body: { on: boolean; flatten: boolean; mode?: string }) => api.post("/api/kill-switch", body),
    onSuccess: (_, v) => {
      // the switch is a setting: pages holding a copy (Risk) must see it now
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(v.on ? "Kill switch engaged: no new entries" : "Trading re-enabled");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (engaged)
    return (
      <Tip content="New entries are blocked. Releasing needs your password.">
        <Button size="sm" variant="danger" className="max-lg:w-9 max-lg:px-0" onClick={() => m.mutate({ on: false, flatten: false, mode })} loading={m.isPending} icon={<ShieldAlert className="size-4" />} aria-label="Trading halted: release">
          <span className="hidden lg:inline">Halted</span>
        </Button>
      </Tip>
    );
  return (
    <DropMenu
      trigger={
        <Button size="sm" variant="ghost" className="border border-down/35 bg-down-soft text-down hover:bg-down hover:text-white max-lg:w-9 max-lg:px-0" icon={<OctagonX className="size-4" />} loading={m.isPending} aria-label="Kill switch">
          <span className="hidden lg:inline">Kill switch</span>
        </Button>
      }
      items={[
        {
          label: "Stop new entries (positions keep their exits)",
          onSelect: async () => {
            if (await confirm({ title: "Engage the kill switch?", body: "Strategies stop opening positions. Open positions keep their stops, targets and square-off.", confirm: "Stop new entries", danger: true }))
              m.mutate({ on: true, flatten: false });
          },
        },
        {
          label: "Stop and close every position",
          danger: true,
          onSelect: async () => {
            if (await confirm({ title: "Close everything now?", body: "Blocks new entries and sends exit orders for every open position, in PAPER and LIVE.", confirm: "Close everything", danger: true, requireText: "CLOSE" }))
              m.mutate({ on: true, flatten: true });
          },
        },
      ]}
    />
  );
}

function ModeSwitch() {
  const view = useLive((s) => s.view);
  const setView = useLive((s) => s.setView);
  const { hasLive } = useKernel();
  return (
    <Segmented
      size="xs"
      value={hasLive ? view : "PAPER"}
      onChange={(v) => (hasLive ? setView(v) : toast.info("LIVE appears once your Angel One account is connected in Settings"))}
      options={[
        { value: "PAPER", label: "Paper" },
        { value: "LIVE", label: "Live", tone: "live" },
      ]}
    />
  );
}

/** The phone's top bar: brand or back, the title once the page's own title has
 * scrolled away, today's P&L, Paper or Live, and the kill switch. */
function MobileBar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { k } = useKernel();
  const pageTitle = useChrome((s) => s.title);
  const onScreen = useChrome((s) => s.titleOnScreen);
  const route = ROUTES.find(([re]) => re.test(loc.pathname));
  const back = route?.[2] ?? null;
  // back as a native app does it: to the screen you came from, else up a level
  const goBack = useBack(back ?? "/");
  const title = pageTitle ?? route?.[1] ?? "AlgoTrader";
  const showTitle = !onScreen;
  return (
    <header className="safe-top shrink-0 border-b border-line bg-panel/95 backdrop-blur md:hidden print:!hidden">
      <div className="flex h-12 items-center gap-1.5 px-2">
        {back ? (
          <button onClick={goBack} className="-ml-0.5 grid size-10 shrink-0 place-items-center rounded-full active:bg-panel-3" aria-label="Back">
            <ChevronLeft className="size-5" />
          </button>
        ) : (
          <div className="shrink-0 pl-1">
            <Brand compact />
          </div>
        )}
        <div className={cn("min-w-0 flex-1 truncate px-1 text-[16px] font-semibold tracking-tight transition-opacity duration-200", showTitle ? "opacity-100" : "opacity-0")}>{title}</div>
        {k && loc.pathname !== "/" && (
          <button onClick={() => nav("/")} className="shrink-0 rounded-lg px-1.5 py-1 text-[13px] font-semibold active:bg-panel-3" aria-label="Today's P&L">
            <Pnl value={k.pnl.total} flash={false} />
          </button>
        )}
        <ModeSwitch />
        <KillSwitch />
      </div>
    </header>
  );
}

function TabBar({ onMore }: { onMore: () => void }) {
  const loc = useLocation();
  const inMore = MORE.some((g) => g.items.some((n) => loc.pathname.startsWith(n.to)));
  const item = (active: boolean, Icon: typeof Menu, label: string) => (
    <>
      <span className={cn("grid h-7 w-12 place-items-center rounded-full transition-colors", active && "bg-accent-soft")}>
        <Icon className="size-[19px]" />
      </span>
      <span className={cn("text-[10.5px]", active ? "font-semibold" : "font-medium")}>{label}</span>
    </>
  );
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur md:hidden print:!hidden">
      <div className="flex h-[58px]">
        {TABS.map(({ to, short, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => cn("flex flex-1 flex-col items-center justify-center gap-0.5", isActive ? "text-accent" : "text-muted")}>
            {({ isActive }) => item(isActive, Icon, short)}
          </NavLink>
        ))}
        <button onClick={onMore} className={cn("flex flex-1 flex-col items-center justify-center gap-0.5", inMore ? "text-accent" : "text-muted")}>
          {item(inMore, Menu, "More")}
        </button>
      </div>
    </nav>
  );
}

function UserMenu({ user, rail, theme, onTheme, onSignOut }: { user: string; rail: boolean; theme: string; onTheme: () => void; onSignOut: () => void }) {
  const nav = useNavigate();
  return (
    <DropMenu
      align="start"
      trigger={
        <button className={cn("flex rounded-lg text-left hover:bg-panel-2", rail ? "min-h-[52px] w-full flex-col items-center justify-center gap-1" : "w-full items-center gap-2.5 px-2 py-1.5")} aria-label="Account">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-accent-soft text-[12px] font-semibold text-accent uppercase">{user.slice(0, 1)}</span>
          {rail && <span className="max-w-full truncate text-[10.5px] leading-none text-muted">Account</span>}
          {!rail && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{user}</span>
              <span className="block text-[11.5px] text-faint">Account</span>
            </span>
          )}
        </button>
      }
      items={[
        { label: theme === "dark" ? "Light theme" : "Dark theme", icon: theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />, onSelect: onTheme },
        { label: "Settings", icon: <Settings className="size-4" />, onSelect: () => nav("/settings") },
        "sep",
        { label: "Sign out", icon: <LogOut className="size-4" />, onSelect: onSignOut },
      ]}
    />
  );
}

export function AppShell({ children, user, onSignOut }: { children: ReactNode; user: string; onSignOut: () => void }) {
  const [more, setMore] = useState(false);
  const [theme, toggleTheme] = useTheme();
  const { k, mode } = useKernel();
  const loc = useLocation();
  const nav = useNavigate();
  const phone = useMediaQuery("(max-width: 767px)");
  // a tablet gets a rail of icons with their names under them; from 1024 px the
  // full sidebar (the tables drop secondary columns to fit beside it)
  const rail = useMediaQuery("(max-width: 1023px)");
  const fullBleed = loc.pathname.startsWith("/charts") || loc.pathname.startsWith("/markets");
  // a phone's full chart takes the whole screen, like a native chart app; its list of light charts is a normal page
  const search = new URLSearchParams(loc.search);
  const fullScreen = phone && ((loc.pathname === "/charts" && search.has("key")) || (loc.pathname === "/markets" && search.has("symbol")));
  const chartList = phone && (loc.pathname === "/charts" || loc.pathname === "/markets") && !fullScreen;
  const padded = !fullBleed || chartList;
  usePriceAlertToasts();
  return (
    <div className="flex h-full print:block print:h-auto">
      <aside className={cn("hidden shrink-0 flex-col border-r border-line bg-panel py-3.5 md:flex print:!hidden", rail ? "w-[88px] px-1.5" : "w-[236px] px-3")}>
        <div className={cn("mb-4 flex items-center", rail ? "justify-center" : "pl-1.5")}>
          <Link to="/" aria-label="Command center">
            <Brand compact={rail} />
          </Link>
        </div>
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <Nav rail={rail} />
        </div>
        {rail ? (
          <div className="mt-3 space-y-1 border-t border-line pt-3">
            <SystemStatus rail />
            <UserMenu user={user} rail theme={theme} onTheme={toggleTheme} onSignOut={onSignOut} />
          </div>
        ) : (
          // one row, so all thirteen pages fit on a 720 px laptop screen
          <div className="mt-3 flex items-center gap-1 border-t border-line pt-3">
            <div className="min-w-0 flex-1">
              <UserMenu user={user} rail={false} theme={theme} onTheme={toggleTheme} onSignOut={onSignOut} />
            </div>
            <SystemStatus dot />
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {!fullScreen && <MobileBar />}
        <header className={cn("hidden h-14 shrink-0 items-center gap-3 border-b border-line bg-panel/80 px-4 backdrop-blur md:flex lg:px-6 print:!hidden", mode === "LIVE" && "border-b-live/50")}>
          <SessionClock />
          <div className="ml-auto flex shrink-0 items-center gap-2.5">
            {k && (
              <button onClick={() => nav("/")} className="flex h-9 items-center gap-2 rounded-lg border border-line bg-panel-2 px-3 hover:border-line-strong" title="Today's net P&L (realized plus open)">
                <span className="hidden text-[12px] text-muted lg:inline">Today</span>
                <Pnl value={k.pnl.total} className="text-[14px] font-semibold" />
              </button>
            )}
            <ModeSwitch />
            <KillSwitch />
          </div>
        </header>
        {mode === "LIVE" && !fullScreen && (
          <div className="flex h-7 shrink-0 items-center justify-center gap-2 bg-live-soft text-[12px] font-medium text-live">
            <Activity className="size-3.5" /> Viewing LIVE: real orders, real money
          </div>
        )}
        <main
          className={cn(
            "min-h-0 flex-1 overflow-y-auto overscroll-y-contain print:overflow-visible",
            // the top padding lives on the content, not the scroller, so whatever
            // a page pins (sticky) sits flush with the top edge
            padded && "px-4 sm:px-6 xl:px-8",
            // room for the phone's tab bar
            fullScreen ? "" : padded ? "pb-[calc(74px+env(safe-area-inset-bottom))] md:pb-10" : "pb-[calc(58px+env(safe-area-inset-bottom))] md:pb-0",
          )}
        >
          {padded ? <div className="mx-auto max-w-[1520px] pt-4 sm:pt-6">{children}</div> : children}
        </main>
      </div>

      {!fullScreen && <TabBar onMore={() => setMore(true)} />}
      <Sheet open={more} onOpenChange={setMore} side="bottom" title="More">
        <div className="space-y-4">
          {MORE.map((g) => (
            <div key={g.label}>
              <div className="mb-2 px-1 text-[11.5px] font-semibold tracking-wider text-faint uppercase">{g.label}</div>
              <div className="grid grid-cols-3 gap-2">
                {g.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMore(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-3.5 text-center text-[12.5px] leading-tight font-medium transition-colors active:scale-[0.98]",
                        isActive ? "border-accent bg-accent-soft text-accent" : "border-line bg-panel-2 text-fg",
                      )
                    }
                  >
                    <Icon className="size-5" />
                    {label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <SystemStatus full />
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-[13px] text-muted">Signed in as {user}</span>
          <Button size="sm" variant="secondary" onClick={toggleTheme} icon={theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}>
            {theme === "dark" ? "Light" : "Dark"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onSignOut} icon={<LogOut className="size-3.5" />}>
            Sign out
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
